import type {
	ColumnDef,
	ColumnSizingState,
	TableOptions,
	Updater,
} from '@tanstack/react-table';
import { getCoreRowModel } from '@tanstack/react-table';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DataTableColumnProps } from './column-template/types';
import type { DataTableSortProps } from './types';

function flattenColumns<T>(columns: ColumnDef<T>[]) {
	// eslint-disable-next-line unicorn/no-array-reduce
	return columns.reduce<string[]>((result, column) => {
		if (column.meta?.sticky) {
			result.push(column.id ?? '');
			if ('columns' in column && column?.columns) {
				result.push(...column.columns.map((subColumn) => subColumn.id ?? ''));
			}
		}
		return result;
	}, []);
}

function useControlledWidth<T>(
	props: Required<DataTableColumnProps<T>>['columnTemplateMeta']
) {
	const [widthState, setWidthState] = useState(props.columnWidths);

	useEffect(() => {
		/**
		 * Prop changes should override component state changes
		 */
		if (!_.isEqual(props.columnWidths, widthState)) {
			setWidthState(props.columnWidths);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.columnWidths]);

	/**
	 * Debounce the callback to prevent a server request
	 * from occurring every single pixel change
	 */
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedChangeColumnWidths = useCallback(
		_.debounce(props.changeColumnWidths, 500),
		[props.changeColumnWidths]
	);

	return {
		columnWidths: widthState,
		setColumnsWidths: useCallback(
			(widthStateUpdater: Updater<ColumnSizingState>) => {
				setWidthState((old) => {
					const newWidthState =
						typeof widthStateUpdater === 'function'
							? widthStateUpdater(old)
							: widthStateUpdater;
					debouncedChangeColumnWidths(newWidthState);
					return newWidthState;
				});
			},
			[debouncedChangeColumnWidths]
		),
	};
}

export interface UseControlledTableOptions<T>
	extends DataTableSortProps, DataTableColumnProps<T> {
	useColumnResizing?: boolean;
}

export function useControlledTable<T>({
	columns,
	columnTemplateMeta,
	sort,
	useColumnResizing,
}: UseControlledTableOptions<T>) {
	const { columnWidths, setColumnsWidths } =
		useControlledWidth(columnTemplateMeta);

	const result = useMemo((): Omit<TableOptions<T>, 'columns' | 'data'> => {
		// If a column is hidden, all of its sub-columns should be hidden as well
		let hiddenSubColumnIds: string[] = [];
		for (const col of columns) {
			if (
				'columns' in col &&
				col.columns?.length &&
				col.meta?.visible === false
			) {
				for (const subCol of col.columns) {
					hiddenSubColumnIds = [...hiddenSubColumnIds, subCol.id!];
				}
			}
		}

		const columnVisibility = Object.fromEntries(
			columns
				.filter((col) => col.meta?.visible === false)
				.map((col) => col.id)
				// eslint-disable-next-line unicorn/prefer-spread
				.concat(hiddenSubColumnIds)
				.map((id) => [id, false])
		);

		return {
			// Our backend doesn't support sorting by multiple columns.
			enableMultiSort: false,
			// We are sorting the data before passing it to useTable.
			manualSorting: true,
			// We are paginating the data before passing it to useTable.
			manualPagination: true,
			// Don't guess the sort direction. Always sort ascending first,
			// unless overridden in the column definition.
			sortDescFirst: false,

			enableColumnResizing: useColumnResizing,
			columnResizeMode: 'onChange',

			// State passed here will merge with the table's internal state
			state: {
				columnVisibility,
				sorting: sort?.sort
					? [{ id: sort.sort, desc: sort.sortDirection === 'desc' }]
					: [],
				columnSizing: useColumnResizing ? columnWidths : {},
				columnPinning: {
					left: flattenColumns(columns),
				},
			},

			getCoreRowModel: getCoreRowModel(),
			onColumnSizingChange: setColumnsWidths,
		};
	}, [columnWidths, columns, setColumnsWidths, sort, useColumnResizing]);
	return result;
}
